import { useEffect, useState } from 'react';

import {
  addDays,
  addMonths,
  addWeeks,
  format,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar as UiCalendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { SelectorButton } from './selector-button';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function DateSelector({
  view,
  date,
  setDate,
  className,
}: {
  view: 'day' | 'week' | 'month';
  date: Date;
  setDate: React.Dispatch<React.SetStateAction<Date>>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(date.getFullYear());
  const triggerText =
    view === 'month' ? format(date, 'MMM yyyy') : format(date, 'MMM d, yyyy');

  useEffect(() => {
    if (!open) return;
    setDisplayYear(date.getFullYear());
  }, [date, open]);

  return (
    <div
      className={cn(
        '@container flex w-full flex-row justify-center',
        className,
      )}
    >
      <SelectorButton
        className="hidden @2xs:flex"
        onClick={() => {
          setDate((date) => {
            if (view === 'day') {
              return subDays(date, 1);
            }

            if (view === 'week') {
              return subWeeks(date, 1);
            }

            return subMonths(date, 1);
          });
        }}
      >
        <ChevronLeft className="size-4" />{' '}
        <span className="hidden @sm:inline">Prev</span>
      </SelectorButton>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              className={cn(
                'h-8 w-44 justify-start rounded-md border-slate-200 bg-white text-sm font-medium shadow-none hover:border-sky-200 hover:bg-sky-50',
              )}
            >
              <span className="truncate">{triggerText}</span>
              <CalendarIcon className="ml-auto size-4 text-slate-500" />
            </Button>
          }
        />
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          {view === 'month' ? (
            <div className="w-64 p-3">
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  className="rounded border border-transparent bg-transparent p-1 text-slate-600 transition-colors hover:bg-sky-50 hover:text-sky-700"
                  onClick={() => setDisplayYear((year) => year - 1)}
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span className="text-sm font-semibold">{displayYear}</span>
                <button
                  type="button"
                  className="rounded border border-transparent bg-transparent p-1 text-slate-600 transition-colors hover:bg-sky-50 hover:text-sky-700"
                  onClick={() => setDisplayYear((year) => year + 1)}
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {MONTH_LABELS.map((label, monthIndex) => {
                  const isSelected =
                    displayYear === date.getFullYear() &&
                    monthIndex === date.getMonth();
                  return (
                    <button
                      key={label}
                      type="button"
                      className={cn(
                        'rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm text-slate-700 transition-colors',
                        isSelected
                          ? 'border-sky-200 bg-sky-100 text-sky-700'
                          : 'hover:border-sky-100 hover:bg-sky-50 hover:text-sky-700',
                      )}
                      onClick={() => {
                        setDate((current) => {
                          const updated = new Date(current);
                          updated.setFullYear(displayYear, monthIndex, 1);
                          return updated;
                        });
                        setOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <UiCalendar
              mode="single"
              selected={date}
              defaultMonth={date}
              captionLayout="dropdown"
              onSelect={(nextDate) => {
                if (!nextDate) return;
                setDate(nextDate);
                setOpen(false);
              }}
            />
          )}
        </PopoverContent>
      </Popover>

      <SelectorButton
        className="hidden @2xs:flex"
        onClick={() => {
          setDate((date) => {
            if (view === 'day') {
              return addDays(date, 1);
            }

            if (view === 'week') {
              return addWeeks(date, 1);
            }

            return addMonths(date, 1);
          });
        }}
      >
        <span className="hidden @sm:inline">Next</span>{' '}
        <ChevronRight className="size-4" />
      </SelectorButton>
    </div>
  );
}
